import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components/macro';
import * as selectAuth from '../../../../auth/selectors';
import { useAnalyticsEvent } from '../../../../../helpers/analytics';
import { clearFocusedHighlight } from '../../actions';
import { cardWidth } from '../../constants';
import Confirmation from '../Confirmation';
import theme from '../../../../theme';
import { HTMLElement } from '@openstax/types/lib.dom';
import { assertWindow } from '../../../../utils';

interface LoginOrEditProps {
  children: React.ReactNode;
  className: string;
  isNewSelection: boolean;
  shouldFocusCard: boolean;
  hasAnnotation: boolean;
  onBlur: typeof clearFocusedHighlight;
  fref: React.ForwardedRef<HTMLElement>;
  elementRef: React.RefObject<HTMLElement>;
  onClick?: () => void;
  'data-testid'?: string;
  'data-active'?: boolean;
  'data-hidden'?: boolean;
  'data-toc-open'?: boolean;
  'data-has-query'?: boolean;
  style?: React.CSSProperties;
}

export function LoginOrEdit({
  children,
  className,
  isNewSelection,
  shouldFocusCard,
  hasAnnotation,
  onBlur,
  fref,
  elementRef,
  onClick,
  ...restProps
}: LoginOrEditProps) {
  const authenticated = !!useSelector(selectAuth.user);
  const { formatMessage } = useIntl();

  const showCard = React.useCallback((event: React.MouseEvent) => {
    if (event.button === 0) {
      event.preventDefault();
      document?.dispatchEvent(new CustomEvent('showCardEvent', { bubbles: true }));
    }
  }, []);

  return (
    <div
      ref={fref as React.Ref<HTMLDivElement>}
      className={className}
      role='dialog'
      aria-label={formatMessage({ id: 'i18n:highlighter:edit-note:label' })}
      onClick={onClick}
      {...restProps}
    >
      {authenticated ? (
        <HiddenOnMobile>
          {shouldFocusCard || hasAnnotation ? (
            <form
              ref={elementRef}
              data-analytics-region='edit-note'
              data-highlight-card
            >
              {children}
            </form>
          ) : (
            <button type='button' className='button-default' onMouseDown={showCard}>
              <FormattedMessage
                id={
                  isNewSelection
                    ? 'i18n:highlighting:create-instructions'
                    : 'i18n:highlighting:instructions'
                }
              />
            </button>
          )}
        </HiddenOnMobile>
      ) : (
        <LoginConfirmation onBlur={onBlur} />
      )}
    </div>
  );
}

export function LoginConfirmation({
  onBlur,
}: {
  onBlur: typeof clearFocusedHighlight;
}) {
  const loginLink = useSelector(selectAuth.loginLink);
  const trackShowLogin = useAnalyticsEvent('showLogin');
  const onCancel = React.useCallback(() => {
    onBlur();
    assertWindow().getSelection()?.removeAllRanges();
  }, [onBlur]);

  React.useEffect(() => {
    trackShowLogin();
  }, [trackShowLogin]);

  return (
    <Confirmation
      data-analytics-label='login'
      data-analytics-region='highlighting-login'
      message='i18n:highlighting:login:prompt'
      confirmMessage='i18n:highlighting:login:link'
      confirmLink={loginLink}
      onCancel={onCancel}
      drawFocus={false}
    />
  );
}

// tslint:disable-next-line:variable-name
export const HiddenOnMobile = styled.div`
  min-width: ${cardWidth}rem;
  ${theme.breakpoints.touchDeviceQuery(css`
    display: none;
  `)}
`;
